"use client";

import * as React from "react";
import { CopyIcon, CheckIcon } from "lucide-react";

import { cn } from "../../lib/utils";

export type CopyButtonProps = Omit<React.ComponentProps<"div">, "children"> & {
	/** Text to copy to the clipboard. */
	value: string;
	/** Controlled copied state. When provided, the component becomes controlled. */
	copied?: boolean;
	/** Callback fired when the copied state changes. */
	onCopiedChange?: (copied: boolean) => void;
	/** Delay in ms before auto-resetting uncontrolled copied state. @default 2000 */
	resetDelay?: number;
	children: React.ReactNode;
};

function CopyButton({
	value,
	copied: copiedProp,
	onCopiedChange,
	resetDelay = 2000,
	children,
	className,
	...props
}: CopyButtonProps) {
	const isControlled = copiedProp !== undefined;
	const [internalCopied, setInternalCopied] = React.useState(false);
	const copied = isControlled ? copiedProp : internalCopied;
	const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

	const setCopied = React.useCallback(
		(next: boolean) => {
			onCopiedChange?.(next);
			if (!isControlled) {
				setInternalCopied(next);
			}
		},
		[isControlled, onCopiedChange],
	);

	React.useEffect(
		() => () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		},
		[],
	);

	const handleCopy = async () => {
		if (copied) {
			return;
		}
		try {
			await navigator.clipboard.writeText(value);
		} catch (error) {
			onCopiedChange?.(false);
			// eslint-disable-next-line no-console -- no logger context in packages/ui
			console.error("Failed to copy to clipboard", error);
			return;
		}
		setCopied(true);

		if (!isControlled) {
			timeoutRef.current = setTimeout(() => {
				setInternalCopied(false);
				onCopiedChange?.(false);
			}, resetDelay);
		}
	};

	return (
		<div
			data-slot="copy-button"
			className={cn("group/copy-button flex items-center", className)}
			{...props}
		>
			{children}
			<div
				className={cn(
					"ml-1.5 transition-opacity",
					copied
						? "opacity-100 pointer-events-auto"
						: "opacity-0 pointer-events-none group-hover/copy-button:opacity-100 group-hover/copy-button:pointer-events-auto group-focus-within/copy-button:opacity-100 group-focus-within/copy-button:pointer-events-auto",
				)}
			>
				<button
					type="button"
					className="flex size-[22px] items-center justify-center rounded bg-transparent outline-none hover:bg-neutral-100 focus-visible:ring-2 focus-visible:ring-neutral-600/50 active:bg-neutral-200/50 [&_svg]:stroke-neutral-500 hover:[&_svg]:stroke-neutral-600"
					onClick={handleCopy}
					aria-label={copied ? "Copied" : "Copy to clipboard"}
				>
					{copied ? (
						<CheckIcon className="size-3" />
					) : (
						<CopyIcon className="size-3" />
					)}
				</button>
			</div>
		</div>
	);
}

export { CopyButton };
